/* iter-121 — WorkspaceLibraryPanel.
 *
 * Pinned at the root of every departmental workspace dialog. Three
 * tabs: Pictures · Documents · Links. Editor / Admin / Engineer can
 * upload + delete; everyone else sees a read-only library.
 *
 * Backend:  GET/POST/DELETE  /api/workspace-library/{rootId}/(files|links)
 */
import { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Upload, Trash2, Link as LinkIcon, Image as ImageIcon, FileText, Plus, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import FilePreviewThumb from './FilePreviewThumb';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'images',    label: 'Pictures',  Icon: ImageIcon },
  { id: 'documents', label: 'Documents', Icon: FileText },
  { id: 'links',     label: 'Links',     Icon: LinkIcon },
];

const EDITOR_LIKE = ['editor', 'admin', 'engineer', 'ownership', 'top_leader'];

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function favicon(url) {
  try {
    const u = new URL(url);
    return `https://www.google.com/s2/favicons?domain=${u.hostname}&sz=64`;
  } catch { return null; }
}

export default function WorkspaceLibraryPanel({ rootId, label, accent = '#1F4FBE' }) {
  const [data, setData] = useState({ files: [], links: [] });
  const [tab, setTab] = useState('images');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [newLink, setNewLink] = useState({ title: '', url: '' });
  const [addingLink, setAddingLink] = useState(false);
  const fileInputRef = useRef(null);

  const token = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const role = typeof window !== 'undefined' ? (localStorage.getItem('user_role') || '').toLowerCase() : '';
  const canEdit = EDITOR_LIKE.includes(role) && !!token;

  const refresh = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/workspace-library/${rootId}`);
      setData({ files: d?.files || [], links: d?.links || [] });
    } catch { /* silent — empty state */ }
  }, [rootId]);

  useEffect(() => { refresh(); }, [refresh]);

  const onFileChange = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setBusy(true);
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setProgress({ current: i + 1, total: files.length, name: f.name });
      try {
        const form = new FormData();
        form.append('file', f);
        // eslint-disable-next-line no-await-in-loop
        await axios.post(`${API}/workspace-library/${rootId}/files`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (err) {
        toast.error(`Upload failed: ${f.name}`);
      }
    }
    setProgress(null);
    setBusy(false);
    await refresh();
    toast.success(`Uploaded ${files.length} file${files.length === 1 ? '' : 's'} to ${label} Library`);
  }, [rootId, token, label, refresh]);

  const deleteFile = async (fid) => {
    if (!window.confirm('Delete this file from the library?')) return;
    try {
      await axios.delete(`${API}/workspace-library/${rootId}/files/${fid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await refresh();
    } catch { toast.error('Could not delete file'); }
  };

  const submitLink = async (e) => {
    e?.preventDefault?.();
    if (!newLink.title.trim() || !newLink.url.trim()) return;
    setAddingLink(true);
    try {
      await axios.post(`${API}/workspace-library/${rootId}/links`, newLink, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNewLink({ title: '', url: '' });
      await refresh();
      toast.success('Link added');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not add link');
    } finally {
      setAddingLink(false);
    }
  };

  const deleteLink = async (lid) => {
    if (!window.confirm('Delete this link?')) return;
    try {
      await axios.delete(`${API}/workspace-library/${rootId}/links/${lid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await refresh();
    } catch { toast.error('Could not delete link'); }
  };

  const images = data.files.filter((f) => f.kind === 'image');
  const documents = data.files.filter((f) => f.kind !== 'image');
  const counts = { images: images.length, documents: documents.length, links: data.links.length };

  return (
    <section
      data-testid={`workspace-library-${rootId}`}
      className="rounded-2xl mb-4 p-4 border"
      style={{ borderColor: `${accent}22`, background: `${accent}08` }}
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-5 rounded-full" style={{ background: accent }} />
          <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: accent, fontFamily: 'Manrope' }}>
            {label} Library
          </h3>
        </div>
        <div className="flex gap-1">
          {TABS.map(({ id, label: tabLabel, Icon }) => {
            const active = tab === id;
            const count = counts[id];
            return (
              <button
                key={id}
                type="button"
                onClick={() => setTab(id)}
                data-testid={`workspace-library-tab-${id}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold transition-colors"
                style={{
                  background: active ? accent : 'white',
                  color: active ? 'white' : accent,
                  border: `1px solid ${accent}33`,
                  fontFamily: 'Manrope',
                }}
              >
                <Icon className="w-3 h-3" />
                {tabLabel}
                <span className="ml-1 opacity-80">({count})</span>
              </button>
            );
          })}
        </div>
      </div>

      {tab === 'images' && (
        <ImageGrid items={images} accent={accent} canEdit={canEdit} onDelete={deleteFile} rootId={rootId} />
      )}
      {tab === 'documents' && (
        <DocList items={documents} accent={accent} canEdit={canEdit} onDelete={deleteFile} rootId={rootId} />
      )}
      {tab === 'links' && (
        <LinkList items={data.links} accent={accent} canEdit={canEdit} onDelete={deleteLink} rootId={rootId} />
      )}

      {/* Footer actions — editor+ only */}
      {canEdit && (tab === 'images' || tab === 'documents') && (
        <div className="mt-3 flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={tab === 'images' ? 'image/*' : undefined}
            onChange={onFileChange}
            className="hidden"
            data-testid={`workspace-library-file-input-${rootId}`}
          />
          <button
            type="button"
            disabled={busy}
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-bold text-white disabled:opacity-60"
            style={{ background: accent, fontFamily: 'Manrope' }}
            data-testid={`workspace-library-upload-${rootId}`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            Upload {tab === 'images' ? 'pictures' : 'documents'}
          </button>
          {progress && (
            <span className="text-[11px]" style={{ color: '#6B7280', fontFamily: 'Manrope' }}>
              {progress.current}/{progress.total} · {progress.name}
            </span>
          )}
        </div>
      )}

      {canEdit && tab === 'links' && (
        <form
          onSubmit={submitLink}
          className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-2"
          data-testid={`workspace-library-link-form-${rootId}`}
        >
          <input
            type="text"
            value={newLink.title}
            onChange={(e) => setNewLink((s) => ({ ...s, title: e.target.value }))}
            placeholder="Link title"
            className="px-3 py-1.5 rounded-md border text-[12px] outline-none"
            style={{ borderColor: `${accent}33`, background: 'white', fontFamily: 'Manrope' }}
            data-testid={`workspace-library-link-title-${rootId}`}
          />
          <input
            type="url"
            value={newLink.url}
            onChange={(e) => setNewLink((s) => ({ ...s, url: e.target.value }))}
            placeholder="https://…"
            className="px-3 py-1.5 rounded-md border text-[12px] outline-none"
            style={{ borderColor: `${accent}33`, background: 'white', fontFamily: 'Manrope' }}
            data-testid={`workspace-library-link-url-${rootId}`}
          />
          <button
            type="submit"
            disabled={addingLink || !newLink.title.trim() || !newLink.url.trim()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-bold text-white disabled:opacity-50"
            style={{ background: accent, fontFamily: 'Manrope' }}
            data-testid={`workspace-library-link-add-${rootId}`}
          >
            <Plus className="w-3.5 h-3.5" />
            Add link
          </button>
        </form>
      )}
    </section>
  );
}

function ImageGrid({ items, accent, canEdit, onDelete, rootId }) {
  if (!items.length) {
    return <EmptyState text="No pictures pinned yet." accent={accent} />;
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
      {items.map((f) => (
        <a
          key={f.id}
          href={`${process.env.REACT_APP_BACKEND_URL}${f.url}`}
          target="_blank"
          rel="noreferrer noopener"
          className="group relative rounded-lg overflow-hidden border bg-white hover:shadow-md transition-shadow"
          style={{ borderColor: '#EAE5D9' }}
          data-testid={`workspace-library-image-${rootId}-${f.id}`}
        >
          <FilePreviewThumb url={f.url} filename={f.filename} size="xl" color={accent} rounded="rounded-none" className="!w-full !h-28" />
          <div className="px-2 py-1.5">
            <p className="text-[11px] truncate font-bold" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>{f.filename}</p>
            <p className="text-[9px]" style={{ color: '#7A8C8C' }}>{fmtSize(f.size_bytes)} · {f.by}</p>
          </div>
          {canEdit && (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); onDelete(f.id); }}
              className="absolute top-1 right-1 p-1 rounded bg-white/90 opacity-0 group-hover:opacity-100 text-red-600 hover:bg-red-50"
              aria-label="Delete"
              data-testid={`workspace-library-image-delete-${f.id}`}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </a>
      ))}
    </div>
  );
}

function DocList({ items, accent, canEdit, onDelete, rootId }) {
  if (!items.length) {
    return <EmptyState text="No documents pinned yet." accent={accent} />;
  }
  return (
    <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
      {items.map((f) => (
        <li
          key={f.id}
          className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-white group"
          style={{ borderColor: '#EAE5D9' }}
          data-testid={`workspace-library-doc-${rootId}-${f.id}`}
        >
          <FilePreviewThumb url={f.url} filename={f.filename} size="md" color={accent} />
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}${f.url}`}
            target="_blank"
            rel="noreferrer noopener"
            className="flex-1 min-w-0"
          >
            <p className="text-[12px] font-bold truncate" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>{f.filename}</p>
            <p className="text-[9.5px]" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>{fmtSize(f.size_bytes)} · by {f.by}</p>
          </a>
          {canEdit && (
            <button
              type="button"
              onClick={() => onDelete(f.id)}
              className="p-1 rounded text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100"
              aria-label="Delete"
              data-testid={`workspace-library-doc-delete-${f.id}`}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

function LinkList({ items, accent, canEdit, onDelete, rootId }) {
  if (!items.length) {
    return <EmptyState text="No links pinned yet." accent={accent} />;
  }
  return (
    <ul className="space-y-1.5">
      {items.map((l) => (
        <li
          key={l.id}
          className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-white group"
          style={{ borderColor: '#EAE5D9' }}
          data-testid={`workspace-library-link-${rootId}-${l.id}`}
        >
          {favicon(l.url) ? (
            <img src={favicon(l.url)} alt="" className="w-5 h-5 rounded-sm flex-shrink-0" />
          ) : (
            <LinkIcon className="w-4 h-4 flex-shrink-0" style={{ color: accent }} />
          )}
          <a
            href={l.url}
            target="_blank"
            rel="noreferrer noopener"
            className="flex-1 min-w-0"
          >
            <p className="text-[12.5px] font-bold truncate" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>{l.title}</p>
            <p className="text-[10px] truncate" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>{l.url}</p>
          </a>
          <ExternalLink className="w-3 h-3 opacity-50" style={{ color: accent }} />
          {canEdit && (
            <button
              type="button"
              onClick={() => onDelete(l.id)}
              className="p-1 rounded text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100"
              aria-label="Delete link"
              data-testid={`workspace-library-link-delete-${l.id}`}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

function EmptyState({ text, accent }) {
  return (
    <p
      className="text-[12px] italic text-center py-6 rounded-lg border-2 border-dashed"
      style={{ color: '#7A8C8C', borderColor: `${accent}33`, fontFamily: 'Manrope' }}
    >
      {text}
    </p>
  );
}
